/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu May 22 2025
 **/

import { useCallback } from "react";
import { PropsOf } from "@emotion/react";

import { usePersistActions } from "@/frameworks/core/persistence";
import { popoverType, usePopover } from "../usePopover";
import { GROUP_POPOVER_ID } from "@/frameworks/core/commons";
import { CallMethodOption } from "@/modules/chat/components/SidebarContents";
import { Configure } from "@/frameworks/libraries";
import usePhoneCall from "./usePhoneCall";

export default function useExtendCallAPIActions() {
	const { users } = usePersistActions();
	const popover = usePopover();
	const phoneCall = usePhoneCall();

	/* 팝업 제공 조건
	 * 발신자: 070서비스 사용 중
	 * 수신자: 휴대전화 번호가 함께 등록됨
	 * 해당 조건이 충족되지 않을 경우, 팝업 미노출 및 RFICE 전화 실행
	 */
	const check_caller_call_method = useCallback(
		async (calleeID: string): Promise<["default"] | ["default", "070"]> => {
			const callee = await users.get_user(calleeID);

			if ((callee?.phoneNumber || []).length > 0) {
				return ["default", "070"];
			}

			return ["default"];
		},
		[]
	);

	const show_call_method_popover = (
		props: Omit<popoverType<keyof typeof GROUP_POPOVER_ID>, "content" | "uniqueID"> & {
			callMethodProps: PropsOf<typeof CallMethodOption>;
		}
	) => {
		const handleClickCallMethodOption = (e: React.MouseEvent<HTMLLIElement>) => {
			props.callMethodProps.onClick(e);
			popover.hide(props.popoverID, "caller_call_method_option");
		};
		const { callMethodProps, ...rest } = props;

		const popoverShowProps = {
			content: (
				<CallMethodOption
					{...callMethodProps}
					onClick={handleClickCallMethodOption}
					popoverID={props.popoverID}
				/>
			),
			...rest,
			...(props.popoverID && {
				popoverID: props.popoverID as GROUP_POPOVER_ID,
				uniqueID: "caller_call_method_option"
			})
		};
		popover.show(popoverShowProps as popoverType<keyof typeof GROUP_POPOVER_ID>);
	};

	const handleCall = async (
		userID: string,
		options?: {
			anchorEl: popoverType<keyof typeof GROUP_POPOVER_ID>["anchorEl"];
			popoverID: popoverType<keyof typeof GROUP_POPOVER_ID>["popoverID"];
			direction: popoverType<keyof typeof GROUP_POPOVER_ID>["direction"];
		}
	) => {
		const methods = await check_caller_call_method(userID);

		if (methods.length > 1) {
			if (!options) {
				return;
			}

			const callee = await users.get_user(userID);
			show_call_method_popover({
				anchorEl: options.anchorEl,
				popoverID: options.popoverID,
				direction: options.direction,
				callMethodProps: {
					popoverID: options.popoverID,
					onClick: (e) => {
						const type = e.currentTarget.dataset["type"];
						if (type === "default") {
							executeDefaultPhoneCall();
						} else if (type === "070") {
							execute070PhoneCall(callee?.phoneNumber || "");
						}
						e.stopPropagation();
						e.preventDefault();
					},
					data: {
						default: {
							value: userID,
							disabled: !methods.some((method) => method === "default")
						},
						"070": {
							value: callee?.phoneNumber || "",
							disabled: !methods.some((method) => method === "070")
						}
					}
				}
			});
		} else {
			executeDefaultPhoneCall();
		}
		async function executeDefaultPhoneCall() {
			const callID = await phoneCall.start_dial({
				type: "normal",
				callerIDs: [userID]
			});
			phoneCall.popup.show({
				callID,
				receiverID: userID,
				callerID: Configure.userID,
				uiType: "floating"
			});
		}

		async function execute070PhoneCall(outboundCallNumber: string) {
			const callID = await phoneCall.start_dial({
				type: "number",
				outboundCallNumber
			});

			phoneCall.popup.show({
				callID,
				callerID: Configure.userID,
				uiType: "floating"
			});
		}
	};

	return {
		process: {
			check_caller_call_method
		},
		ui: {
			show_call_method_popover,
			common: {
				handleCall
			}
		}
	};
}
